import React from 'react'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group"
import { Label } from "../../../ui/label"
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "../../../ui/tooltip"

/**
 * InpaintingForm Component
 * 
 * A form component for configuring and submitting inpainting operations.
 * 
 * @component
 * @example
 * 
 * // Component usage
 * <InpaintingForm
 *   inpaintingParams={{
 *     inputPrompt: "a beautiful sunset",
 *     negativePrompt: "blurry, low quality",
 *     steps: 50,
 *     guidanceScale: 7.5,
 *     seed: 12345,
 *     model: "sdxl21"  // or "flux1d"
 *   }}
 *   onParamsChange={(newParams) => {
 *     // Handle params change
 *   }}
 *   onSubmit={() => {
 *     // Handle form submission
 *   }}
 *   isSubmitting={false}
 * />
 */
const InpaintingForm = ({
  inpaintingParams,
  onParamsChange,
  onSubmit,
  onBack,
  isSubmitting = false
}) => {
  const handleSubmit = async (e) => {
    e.preventDefault()
    onSubmit()
  }

  const handleModelChange = (value) => {
    if (value) {
      onParamsChange({
        ...inpaintingParams,
        model: value
      })
    }
  }

  const getValidationMessages = () => {
    const messages = []
    
    if (!inpaintingParams.model) {
      messages.push('Choose an AI model for inpainting')
    }
    
    if (!inpaintingParams.inputPrompt.trim()) {
      messages.push('Describe what you want to generate in the selected area')
    }

    return messages
  }

  const validationMessages = getValidationMessages()
  const isValid = validationMessages.length === 0

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Apply Mask</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Model Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-200">Model</Label>
          <p className="text-xs text-gray-400">
            Choose the AI model to use for inpainting
          </p>
          <TooltipProvider>
            <ToggleGroup
              type="single"
              value={inpaintingParams.model}
              onValueChange={handleModelChange}
              className="flex gap-2"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="sdxl21"
                    className={`px-3 py-2 rounded-md transition-colors
                      ${inpaintingParams.model === 'sdxl21'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  >
                    SDXL2.1
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Stable Diffusion XL 2.1 - High quality, versatile model</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleGroupItem
                    value="flux1d"
                    className={`px-3 py-2 rounded-md transition-colors
                      ${inpaintingParams.model === 'flux1d'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  >
                    flux.1 dev
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Flux.1 Dev - Fast, experimental model with advanced features</p>
                </TooltipContent>
              </Tooltip>
            </ToggleGroup>
          </TooltipProvider>
        </div>

        {/* Input Prompt */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">
            Input Prompt
          </label>
          <p className="text-xs text-gray-400">
            Change the original prompt to include your desired edits
          </p>
          <textarea
            value={inpaintingParams.inputPrompt}
            onChange={(e) => onParamsChange({
              ...inpaintingParams,
              inputPrompt: e.target.value
            })}
            className="w-full p-3 bg-gray-700 rounded-md border border-gray-600 
              text-white resize-none text-sm focus:border-blue-500 
              focus:ring-1 focus:ring-blue-500 transition-colors"
            rows="3"
            placeholder="Describe what you want to generate..."
            required
          />
        </div>

        {/* Negative Prompt */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">
            Negative Prompt
          </label>
          <p className="text-xs text-gray-400">
            The prompt or prompts not to guide the image generation
          </p>
          <textarea
            value={inpaintingParams.negativePrompt}
            onChange={(e) => onParamsChange({
              ...inpaintingParams,
              negativePrompt: e.target.value
            })}
            className="w-full p-3 bg-gray-700 rounded-md border border-gray-600 
              text-white resize-none text-sm focus:border-blue-500 
              focus:ring-1 focus:ring-blue-500 transition-colors"
            rows="3"
            placeholder="Describe what to avoid..."
          />
        </div>

        {/* Steps Slider */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-200">
              Inference Steps
            </label>
            <span className="text-sm text-gray-400">
              {inpaintingParams.steps}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            Number of denoising steps (higher = better quality, slower)
          </p>
          <input
            type="range"
            min="1"
            max="500"
            value={inpaintingParams.steps}
            onChange={(e) => onParamsChange({
              ...inpaintingParams,
              steps: parseInt(e.target.value)
            })}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 
              [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white 
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
          />
        </div>

        {/* Guidance Scale Slider */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-200">
              Guidance Scale
            </label>
            <span className="text-sm text-gray-400">
              {inpaintingParams.guidanceScale}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            How closely to follow the prompt (higher = more faithful to prompt)
          </p>
          <input
            type="range"
            min="1"
            max="20"
            step="0.5"
            value={inpaintingParams.guidanceScale}
            onChange={(e) => onParamsChange({
              ...inpaintingParams,
              guidanceScale: parseFloat(e.target.value)
            })}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 
              [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white 
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
          />
        </div>

        {/* Seed Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">
            Seed
          </label>
          <p className="text-xs text-gray-400">
            Random seed for reproducible results (optional)
          </p>
          <input
            type="number"
            value={inpaintingParams.seed}
            onChange={(e) => onParamsChange({
              ...inpaintingParams,
              seed: e.target.value
            })}
            className="w-full p-2 bg-gray-700 rounded-md border border-gray-600 
              text-white text-sm focus:border-blue-500 focus:ring-1 
              focus:ring-blue-500 transition-colors"
            placeholder="Enter seed number (optional)"
          />
        </div>

        {/* Submit Button and Validation Messages */}
        <div className="space-y-3">
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`w-full p-3 rounded-lg transition-colors font-medium
              ${isValid 
                ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                : 'bg-gray-700 text-gray-300 cursor-not-allowed'}`}
          >
            {isSubmitting ? 'Starting Inpainting...' : 'Start Generative Inpainting'}
          </button>

          {/* Validation Messages */}
          {!isValid && (
            <div className="space-y-2 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Before starting inpainting:
                </span>
              </div>
              <ul className="space-y-1.5 pl-6">
                {validationMessages.map((message, index) => (
                  <li 
                    key={index}
                    className="text-sm text-red-400 list-disc"
                  >
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

export default InpaintingForm 